import { useCallback, useEffect, MouseEventHandler, useRef } from 'react';
import { ReactMouseEvent } from '../../react-synthetic-events';

/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const handlers = useSingleAndDoubleClick({
 *     onSingleClick() {
 *       console.log('single click!');
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return <button {...handlers}>Click Me!</button>;
 * }
 * ```
 */
export function useSingleAndDoubleClick(props: {
  onSingleClick: () => void;
  onDoubleClick: (event: ReactMouseEvent) => void;
  persist?: false;
}): { onClick: MouseEventHandler; onDoubleClick: MouseEventHandler };
/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * Setting `persist: true` changes the onSingleClick parameters. It will
 * be called with the MouseEvent from the first click.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const handlers = useSingleAndDoubleClick({
 *     persist: true,
 *     onSingleClick(event) {
 *       console.log(`single click: ${event.clientX}`);
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return <button {...handlers}>Click Me!</button>;
 * }
 * ```
 */
export function useSingleAndDoubleClick(props: {
  onSingleClick: (event: ReactMouseEvent) => void;
  onDoubleClick: (event: ReactMouseEvent) => void;
  persist: true;
}): { onClick: MouseEventHandler; onDoubleClick: MouseEventHandler };
/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * The 2nd argument to the `onClick` event handler returned from this hook
 * will be passed to onSingleClick.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const { onClick, onDoubleClick } = useSingleAndDoubleClick({
 *     persist: true,
 *     onSingleClick(clientX) {
 *       console.log(`single click: ${clientX}`);
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return (
 *     <button
 *       onClick={(event) => {
 *         onClick(event, event.clientX);
 *       }}
 *       onDoubleClick={onDoubleClick}
 *     >
 *       Click Me!
 *     </button>
 *   );
 * }
 * ```
 */
export function useSingleAndDoubleClick<E>(props: {
  onSingleClick: (snapshot: E) => void;
  onDoubleClick: (event: ReactMouseEvent) => void;
  persist?: false;
}): { onClick: (event: ReactMouseEvent, snapshot: E) => void; onDoubleClick: MouseEventHandler };
export function useSingleAndDoubleClick<E>(props: {
  onSingleClick: (snapshot: E) => void;
  onDoubleClick: (event: ReactMouseEvent) => void;
  persist?: boolean;
}) {
  const refSingleClickCallback = useRef<undefined | ((snapshot: E) => void)>(props.onSingleClick);
  useEffect(() => {
    refSingleClickCallback.current = props.onSingleClick;
    return () => (refSingleClickCallback.current = undefined);
  }, [props.onSingleClick]);

  const refClickCount = useRef(0);
  const refTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onClick = useCallback(
    (event: ReactMouseEvent, snapshot: E) => {
      refClickCount.current += 1;

      if (refTimeout.current !== undefined) {
        clearTimeout(refTimeout.current);
        refTimeout.current = undefined;
      }

      if (props.persist) {
        event.persist();
        snapshot = (event as unknown) as E;
      }

      refTimeout.current = setTimeout(() => {
        refTimeout.current = undefined;
        refClickCount.current = 0;
        const onSingleClick = refSingleClickCallback.current;
        if (onSingleClick) onSingleClick(snapshot);
      }, 250);
    },
    [props.persist]
  );

  const onDoubleClick: MouseEventHandler = useCallback(
    (event) => {
      if (refClickCount.current < 2) return;
      refClickCount.current = 0;

      if (refTimeout.current === undefined) return;
      clearTimeout(refTimeout.current);
      refTimeout.current = undefined;

      const onDoubleClick = props.onDoubleClick;
      onDoubleClick(event);
    },
    [props.onDoubleClick]
  );

  return { onClick, onDoubleClick };
}
